/**
 * 이슈 첨부 컴포저 헬퍼 — 붙여넣기/드래그된 이미지를 본문 마크다운으로
 * 엮는 순수 함수들. 업로드 자체(비동기)는 페이지가 오케스트레이션하고,
 * 여기는 텍스트 조작 규칙만 고정한다.
 */
import { IssueLimit } from '@/stubs/issue/issue_pb';

/** 받는 형식의 표시(`image/png` → `PNG`) — 목록은 서버의 정책(`fetchAttachmentPolicy`)이고 여기는 보이는 꼴만 정한다 */
export const attachmentTypesLabel = (types: readonly string[]): string => types.map((t) => (t.split('/')[1] ?? t).toUpperCase()).join('/');

/** 적을 형식이 있는가 — 모르거나(정책의 답 없음) 비면 형식을 적지 않는다 */
const namedTypes = (types: readonly string[] | null): types is readonly string[] => !!types && types.length > 0;

/** 받지 않는 형식의 안내 — 적을 형식이 없으면 형식을 적지 않는다 */
export const attachmentTypeRefusal = (types: readonly string[] | null): string =>
  namedTypes(types) ? `${attachmentTypesLabel(types)} 이미지만 첨부할 수 있습니다` : '이미지만 첨부할 수 있습니다';

/** 입력 칸의 첨부 안내 — 형식은 서버의 정책에서, 상한은 proto 선언에서 */
export const attachmentHint = (types: readonly string[] | null): string =>
  `이미지는 붙여넣기(Ctrl+V)·드래그로 첨부됩니다 (${namedTypes(types) ? `${attachmentTypesLabel(types)}, ` : ''}${ATTACHMENT_MAX_LABEL} 이하)`;

/** 첨부 하나의 상한 — 정본은 proto 선언(IssueLimit) 하나이고 BFF 가 같은 값을 강제한다. 초과분은 업로드 시도 없이 즉시 안내 */
export const ATTACHMENT_MAX_BYTES: number = IssueLimit.ATTACHMENT_MAX_BYTES;

/** 상한의 표시 — 안내 문구는 숫자를 따로 적지 않고 이것을 쓴다 */
export const ATTACHMENT_MAX_LABEL = `${ATTACHMENT_MAX_BYTES / (1024 * 1024)} MiB`;

/** 상한을 넘은 첨부의 안내 */
export const ATTACHMENT_TOO_LARGE_MESSAGE = `${ATTACHMENT_MAX_LABEL} 이하 이미지만 첨부할 수 있습니다`;

/**
 * 커서 위치에 삽입 — 이미지 참조는 별도 줄이어야 렌더가 깔끔하므로
 * 앞뒤가 줄 경계가 아니면 개행으로 띄운다. caret 이 null 이거나 범위 밖이면
 * 끝에 덧붙인다(드래그 앤 드롭 등 커서를 모르는 경로).
 */
export function insertAtCursor(current: string, insert: string, caret: number | null): string {
  const pos = caret !== null && caret >= 0 && caret <= current.length ? caret : current.length;
  const before = current.slice(0, pos);
  const after = current.slice(pos);
  const leading = before && !before.endsWith('\n') ? '\n' : '';
  const trailing = after && !after.startsWith('\n') ? '\n' : '';
  return before + leading + insert + trailing + after;
}

/**
 * 자리표시자 토큰을 결과 참조로 1회 치환.
 * String.replace 의 `$` 치환 패턴 해석을 피하려고 인덱스 접합으로 구현 —
 * 파일명에 `$&` 류가 있어도 원문 그대로 들어간다.
 */
export function replaceOnce(text: string, token: string, replacement: string): string {
  const at = text.indexOf(token);
  if (at < 0) return text;
  return text.slice(0, at) + replacement + text.slice(at + token.length);
}

/**
 * 업로드 자리표시자 — 업로드가 끝나면 첨부 참조로 치환되고, 실패하면 걷힌다.
 * **제출 잠금의 근거가 아니다**: 본문 글자는 사용자가 지우거나(잠금 해제) 손으로 칠 수
 * 있어(거짓 잠금) 잠금은 진행 중인 업로드 *상태*(IssueBoard 의 초안 키별 카운터)가 정한다
 * (게이트 리뷰 #498 2차 B-1·B-2).
 */
export function uploadPlaceholder(seq: number): string {
  return `![업로드 중… ${seq}]()`;
}
